import React from "react";
import {
  INVOICE_ADJUSTMENT_KIND_LABELS,
  INVOICE_ADJUSTMENT_STATUS_LABELS,
  INVOICE_MODIFY_CODE_LABELS,
  NTS_STATE_LABELS,
  type InvoiceAdjustment,
  type InvoiceModifyCode,
} from "../../../../domain/types/tax-document";
import { resolveErrorMessage } from "../../../../shared/utils/save-flow";
import ConfirmMessageModal from "../../UI/ConfirmMessageModal";
import { taxDocumentApi } from "./tax-document-api";

interface Props {
  companyId: number;
  invoiceId: number;
  /** 조정 처리 후 원본 증빙(처리 대기 건수 등)을 다시 받아야 할 때 */
  onProcessed: () => void;
}

/** 이 일수 안에 기한이 오면 "기한 임박" 으로 표시한다 */
const DUE_SOON_DAYS = 5;

const isPending = (a: InvoiceAdjustment) => a.status === "DRAFT" || a.status === "FAILED";

const daysUntil = (date: string) => {
  const [y, m, d] = date.slice(0, 10).split("-").map(Number);
  const due = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
};

const formatAmount = (value: number) => `${value < 0 ? "-" : ""}${Math.abs(value).toLocaleString()}원`;

const executeLabel = (a: InvoiceAdjustment) => (a.kind === "TAX_MODIFY" ? "수정세금계산서 발급" : "취소 실행");

/**
 * 원본 증빙에 딸린 환불 조정 건(취소 / 부분취소 / 수정세금계산서).
 *
 * 주문에 부분 환불이 기록되면 서버가 초안(DRAFT)을 만든다. 담당자가 여기서 확인하고 실행하거나 폐기한다.
 * 수정세금계산서는 사유(modifyCode)와 작성일자를 고른 뒤 발급한다.
 */
export const InvoiceAdjustmentSection: React.FC<Props> = ({ companyId, invoiceId, onProcessed }) => {
  const [items, setItems] = React.useState<InvoiceAdjustment[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  /** 수정세금계산서 발급 전 입력값. 조정 건 id 별 */
  const [modifyInputs, setModifyInputs] = React.useState<
    Record<number, { modifyCode: InvoiceModifyCode; writeDate: string }>
  >({});
  const [confirmMessage, setConfirmMessage] = React.useState("");
  const pendingAction = React.useRef<(() => void) | null>(null);

  const load = React.useCallback(async () => {
    try {
      const list = await taxDocumentApi.adjustments(companyId, invoiceId);
      setItems(list);
      setModifyInputs((prev) => {
        const next = { ...prev };
        list.forEach((a) => {
          if (a.kind === "TAX_MODIFY" && !next[a.id]) {
            next[a.id] = { modifyCode: a.modifyCode ?? "2", writeDate: a.writeDate?.slice(0, 10) ?? "" };
          }
        });
        return next;
      });
    } catch (e) {
      setError(resolveErrorMessage(e, "환불 조정 내역을 불러오지 못했습니다."));
    } finally {
      setLoading(false);
    }
  }, [companyId, invoiceId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const askConfirm = (message: string, action: () => void) => {
    pendingAction.current = action;
    setConfirmMessage(message);
  };

  const run = async (adjustment: InvoiceAdjustment, action: () => Promise<InvoiceAdjustment>, done: string) => {
    setBusyId(adjustment.id);
    setError(null);
    setNotice(null);
    try {
      const result = await action();
      setNotice(result.status === "FAILED" ? `처리에 실패했습니다. ${result.errorMessage ?? ""}`.trim() : done);
    } catch (e) {
      setError(resolveErrorMessage(e, "처리에 실패했습니다."));
    } finally {
      setBusyId(null);
      // 성공·실패 모두 서버 상태가 바뀌었을 수 있다
      await load();
      onProcessed();
    }
  };

  const handleExecute = (a: InvoiceAdjustment) => {
    const input = modifyInputs[a.id];
    if (a.kind === "TAX_MODIFY" && !input?.writeDate) {
      setError("수정세금계산서 작성일자를 입력해 주세요.");
      return;
    }
    const message =
      a.kind === "TAX_MODIFY"
        ? `수정세금계산서(${INVOICE_MODIFY_CODE_LABELS[input.modifyCode]}, ${formatAmount(a.totalAmount)})를 국세청으로 발급하시겠습니까? 발급 후에는 되돌릴 수 없습니다.`
        : `${INVOICE_ADJUSTMENT_KIND_LABELS[a.kind]}(${formatAmount(a.totalAmount)})를 실행하시겠습니까? 실행 후에는 되돌릴 수 없습니다.`;
    askConfirm(message, () =>
      run(
        a,
        () =>
          taxDocumentApi.executeAdjustment(
            companyId,
            a.id,
            a.kind === "TAX_MODIFY" ? { modifyCode: input.modifyCode, writeDate: input.writeDate } : {},
          ),
        `${INVOICE_ADJUSTMENT_KIND_LABELS[a.kind]}를 처리했습니다.`,
      ),
    );
  };

  const handleDiscard = (a: InvoiceAdjustment) =>
    askConfirm(
      `${INVOICE_ADJUSTMENT_KIND_LABELS[a.kind]} 건을 폐기하시겠습니까? 폐기하면 증빙은 그대로 두고 이 조정 건만 닫습니다.`,
      () => run(a, () => taxDocumentApi.discardAdjustment(companyId, a.id), "조정 건을 폐기했습니다."),
    );

  if (loading || (items.length === 0 && !error)) return null;

  return (
    <section>
      <h4 className="text-[14px] font-bold text-[#222] mb-2">환불 조정</h4>
      {error && (
        <div className="mb-2 rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-3 py-2 text-[13px] text-[#4285F4]">{error}</div>
      )}
      {notice && (
        <div className="mb-2 rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-3 py-2 text-[13px] text-[#4285F4]">{notice}</div>
      )}

      <div className="flex flex-col gap-2">
        {items.map((a) => {
          const pending = isPending(a);
          const dueSoon = pending && a.dueDate !== null && daysUntil(a.dueDate) <= DUE_SOON_DAYS;
          const input = modifyInputs[a.id];
          const busy = busyId === a.id;
          return (
            <div
              key={a.id}
              className={`rounded-[8px] border p-3 text-[13px] ${pending ? "border-[#4285F4]/40" : "border-[#e0e0e0]"}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-[#222]">{INVOICE_ADJUSTMENT_KIND_LABELS[a.kind]}</span>
                <StatusBadge status={a.status} />
                {a.ntsState && <span className="text-[11px] text-[#888]">국세청 {NTS_STATE_LABELS[a.ntsState]}</span>}
                <span className="flex-1" />
                <span className="font-bold text-[#222]">{formatAmount(a.totalAmount)}</span>
              </div>

              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#888]">
                <span>
                  공급가액 {formatAmount(a.supplyAmount)} / 부가세 {formatAmount(a.vatAmount)}
                </span>
                {a.dueDate && (
                  <span className={dueSoon ? "font-medium text-[#4285F4]" : undefined}>
                    처리 기한 {a.dueDate.slice(0, 10)}
                    {dueSoon && " · 기한 임박"}
                  </span>
                )}
                {a.status === "DONE" && a.approvalNo && <span>승인번호 {a.approvalNo}</span>}
                {a.status === "DONE" && a.modifyCode && <span>{INVOICE_MODIFY_CODE_LABELS[a.modifyCode]}</span>}
              </div>

              {a.errorMessage && pending && <p className="mt-1.5 text-[12px] text-[#4285F4]">실패 사유: {a.errorMessage}</p>}

              {pending && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {a.kind === "TAX_MODIFY" && input && (
                    <>
                      <select
                        value={input.modifyCode}
                        disabled={busy}
                        onChange={(e) =>
                          setModifyInputs((prev) => ({
                            ...prev,
                            [a.id]: { ...input, modifyCode: e.target.value as InvoiceModifyCode },
                          }))
                        }
                        className="h-[32px] px-2 text-[12px] border border-[#e0e0e0] rounded-[8px] focus:border-[#4285F4] focus:outline-none"
                        aria-label="수정 사유"
                      >
                        {(Object.keys(INVOICE_MODIFY_CODE_LABELS) as InvoiceModifyCode[]).map((code) => (
                          <option key={code} value={code}>
                            {code} {INVOICE_MODIFY_CODE_LABELS[code]}
                          </option>
                        ))}
                      </select>
                      <input
                        type="date"
                        value={input.writeDate}
                        disabled={busy}
                        onChange={(e) =>
                          setModifyInputs((prev) => ({ ...prev, [a.id]: { ...input, writeDate: e.target.value } }))
                        }
                        className="h-[32px] px-2 text-[12px] border border-[#e0e0e0] rounded-[8px] focus:border-[#4285F4] focus:outline-none"
                        aria-label="작성일자"
                      />
                    </>
                  )}
                  <span className="flex-1" />
                  <button
                    disabled={busy}
                    onClick={() => handleDiscard(a)}
                    className="px-2 h-[32px] text-[12px] text-[#F24A4A] hover:text-[#D63939] disabled:opacity-50"
                  >
                    폐기
                  </button>
                  <button
                    disabled={busy || !a.executable}
                    onClick={() => handleExecute(a)}
                    className="px-3 h-[32px] text-[12px] rounded-[6px] bg-[#222] text-white hover:bg-[#333] disabled:bg-[#ccc]"
                  >
                    {busy ? "처리 중..." : executeLabel(a)}
                  </button>
                </div>
              )}
              {pending && !a.executable && a.notExecutableReason && (
                <p className="mt-1.5 text-[12px] text-[#888] text-right">{a.notExecutableReason}</p>
              )}
            </div>
          );
        })}
      </div>

      {confirmMessage && (
        <ConfirmMessageModal
          message={confirmMessage}
          setMessage={setConfirmMessage}
          submitFunction={() => {
            const action = pendingAction.current;
            pendingAction.current = null;
            setConfirmMessage("");
            action?.();
          }}
        />
      )}
    </section>
  );
};

const StatusBadge: React.FC<{ status: InvoiceAdjustment["status"] }> = ({ status }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded-[4px] text-[11px] font-medium ${
      status !== "DISCARDED" ? "bg-[#E8EFFF] text-[#4285F4]" : "bg-[#f5f5f5] text-[#888]"
    }`}
  >
    {INVOICE_ADJUSTMENT_STATUS_LABELS[status]}
  </span>
);
